import { useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import DashboardWrapper from '../Dashboard/components/DashboardWrapper'
import InnerBanner from '../../components/InnerBanner'
import { PRIVATE_ROUTES } from '../../Routes/routes'
import InvoiceTable from '../Accounting/components/InvoiceTable'
import Pagination from '../../components/Pagination'
import UploadInvoiceModal from './components/UploadInvoiceModal'
import EditInvoiceModal from './components/EditInvoiceModal'
import Toast from '../../components/Toast/Toast'
import {
  getMasterInvoices,
  updateMasterInvoice,
  uploadCompanyInvoice,
} from '../../services/NewServicesCompnay'
import { INVOICE_STATUS_ID, mapInvoiceFromApi } from '../../utils/invoiceStatus'

const StatChip = ({ label, value, accent }) => (
  <div
    className="rounded px-3 py-2 d-flex flex-column gap-1"
    style={{
      background: accent ? '#f5f3ff' : '#f9fafb',
      border: `1px solid ${accent ? '#ddd6fe' : '#e5e7eb'}`,
      minWidth: '120px',
    }}
  >
    <span style={{ fontSize: '11px', color: '#6b7280', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
      {label}
    </span>
    <span style={{ fontSize: '20px', fontWeight: '800', color: accent ? '#7c3aed' : '#111827' }}>
      {value}
    </span>
  </div>
)

const ITEMS_PER_PAGE = 10
const MAX_PAGES = 500 // Tope de seguridad para el bucle de carga.

const Billing = () => {
  const { companyId } = useParams()
  const [masterInvoices, setMasterInvoices] = useState([])
  const [isLoadingMasterInvoices, setIsLoadingMasterInvoices] = useState(true)
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [editingInvoice, setEditingInvoice] = useState(null)
  const [isUploadingInvoice, setIsUploadingInvoice] = useState(false)
  const [isSavingInvoice, setIsSavingInvoice] = useState(false)
  const [page, setPage] = useState(0)
  // Por defecto: desde el 1 de enero de 2025 hasta el último día del mes en curso.
  const [dateAfter, setDateAfter] = useState("")
  const [dateBefore, setDateBefore] = useState("")

  const loadMasterInvoices = useCallback(async () => {
    setIsLoadingMasterInvoices(true)

    // El backend pagina de a ITEMS_PER_PAGE. Recorremos todas las páginas para
    // que los contadores (Total/Pendientes/Pagadas) reflejen el total real y no
    // se queden topados en la primera página.
    const all = []
    let currentPage = 0
    let pageSize = null

    while (currentPage < MAX_PAGES) {
      const { response, res } =
        (await getMasterInvoices(currentPage, { companyId, dateAfter, dateBefore })) || {}
      if (!response?.ok || !Array.isArray(res?.list)) break

      all.push(...res.list)
      if (pageSize === null) pageSize = res.list.length
      // Última página: vino incompleta (o vacía).
      if (res.list.length === 0 || res.list.length < pageSize) break
      currentPage += 1
    }

    setMasterInvoices(all.map(mapInvoiceFromApi))
    setPage(0)
    setIsLoadingMasterInvoices(false)
  }, [companyId, dateAfter, dateBefore])

  useEffect(() => {
    loadMasterInvoices()
  }, [loadMasterInvoices])

  const handleSaveEdit = async ({ id, status, invoiceNumber, file }) => {
    setIsSavingInvoice(true)
    try {
      const { response, res } =
        (await updateMasterInvoice({ id, status, invoiceNumber, file })) || {}
      if (!response?.ok) {
        Toast({
          icon: 'error',
          title: res?.msg || res?.message || 'No se pudo actualizar la factura',
          timer: 2500,
        })
        return
      }

      Toast({ title: 'Factura actualizada correctamente', timer: 2000 })
      setEditingInvoice(null)

      await loadMasterInvoices()
    } catch {
      Toast({ icon: 'error', title: 'No se pudo actualizar la factura', timer: 2500 })
    } finally {
      setIsSavingInvoice(false)
    }
  }

  const handleUploadInvoice = async ({ file }) => {
    if (!companyId) {
      Toast({ icon: 'error', title: 'No se encontro la empresa para subir la factura.' })
      return
    }

    setIsUploadingInvoice(true)
    try {
      const data = await uploadCompanyInvoice({ file, companyId })
      if (data?.response?.ok) {
        Toast({ title: 'Factura subida correctamente', timer: 2000 })
        setShowUploadModal(false)

        await loadMasterInvoices()
      } else {
        Toast({ icon: 'error', title: data?.res?.message || 'Error al subir la factura', timer: 2500 })
      }
    } catch (error) {
      Toast({ icon: 'error', title: 'Error al subir la factura', timer: 2500 })
    } finally {
      setIsUploadingInvoice(false)
    }
  }

  const pendingCount = masterInvoices.filter(
    (i) => Number(i.statusId) !== INVOICE_STATUS_ID.PAGADA
  ).length
  const paidCount = masterInvoices.filter(
    (i) => Number(i.statusId) === INVOICE_STATUS_ID.PAGADA
  ).length

  // Más recientes primero (sin mutar el estado) y recorte de la página actual.
  const orderedInvoices = [...masterInvoices].reverse()
  const paginatedInvoices = orderedInvoices.slice(
    page * ITEMS_PER_PAGE,
    page * ITEMS_PER_PAGE + ITEMS_PER_PAGE
  )

  return (
    <DashboardWrapper>
      <div className="p-3 p-lg-4" style={{ maxWidth: '1200px', margin: '0 auto' }}>

        <InnerBanner
          before="Inicio"
          beforeRoute={PRIVATE_ROUTES.dashboard}
          text="Facturación"
          className="mb-3"
        />

        <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-4 gap-3">
          <h2 className="text-bg-brand mb-0" style={{ fontWeight: 800, fontSize: '22px' }}>
            Historial de facturas
          </h2>

          <div className="d-flex gap-2 flex-wrap">
            <StatChip label="Total"      value={masterInvoices.length} />
            <StatChip label="Pendientes" value={pendingCount} accent={pendingCount > 0} />
            <StatChip label="Pagadas"    value={paidCount} />
          </div>
        </div>

        <div className="row g-4 align-items-start">
          <div className="col-12 col-lg-4">
            <div className="bg-white rounded border p-4 h-100" style={{ borderColor: '#e5e7eb' }}>
              <p className="mb-1" style={{ fontWeight: 700, fontSize: '15px', color: '#111827' }}>
                Subir factura
              </p>
              <p className="mb-4" style={{ fontSize: '13px', color: '#6b7280' }}>
                Sube una factura asociada a la empresa seleccionada.
              </p>
              <button
                type="button"
                className="btn btn-bg-CTA-primary w-100"
                onClick={() => setShowUploadModal(true)}
              >
                + Subir factura
              </button>
            </div>
          </div>

          <div className="col-12">
            <div className="bg-white rounded border p-4" style={{ borderColor: '#e5e7eb' }}>
              <div className="d-flex flex-column flex-sm-row align-items-start align-items-sm-end gap-3 mb-4">
                <div className="d-flex flex-column gap-1">
                  <label htmlFor="dateAfter" style={{ fontSize: '12px', fontWeight: 600, color: '#6b7280' }}>
                    Desde
                  </label>
                  <input
                    id="dateAfter"
                    type="date"
                    className="form-control"
                    value={dateAfter}
                    max={dateBefore || undefined}
                    onChange={(e) => setDateAfter(e.target.value)}
                  />
                </div>
                <div className="d-flex flex-column gap-1">
                  <label htmlFor="dateBefore" style={{ fontSize: '12px', fontWeight: 600, color: '#6b7280' }}>
                    Hasta
                  </label>
                  <input
                    id="dateBefore"
                    type="date"
                    className="form-control"
                    value={dateBefore}
                    min={dateAfter || undefined}
                    onChange={(e) => setDateBefore(e.target.value)}
                  />
                </div>
                {(dateAfter || dateBefore) && (
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => {
                      setDateAfter('')
                      setDateBefore('')
                    }}
                  >
                    Limpiar
                  </button>
                )}
              </div>
              <InvoiceTable
                invoices={paginatedInvoices}
                isLoading={isLoadingMasterInvoices}
                onEdit={setEditingInvoice}
                showCompany
              />

              {!isLoadingMasterInvoices && masterInvoices.length > ITEMS_PER_PAGE && (
                <div className="d-flex justify-content-center mt-3">
                  <Pagination
                    totalItems={masterInvoices.length}
                    page={page}
                    setPage={setPage}
                    itemsPerPage={ITEMS_PER_PAGE}
                    isGlobalState
                  />
                </div>
              )}
            </div>
          </div>
        </div>

      </div>

      <EditInvoiceModal
        show={!!editingInvoice}
        invoice={editingInvoice}
        onClose={() => setEditingInvoice(null)}
        onSave={handleSaveEdit}
        isSaving={isSavingInvoice}
      />

      <UploadInvoiceModal
        show={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onSubmit={handleUploadInvoice}
        isLoading={isUploadingInvoice}
      />

    </DashboardWrapper>
  )
}

export default Billing
